import React, { useState, useEffect } from 'react';
import { getSubscriptionPackages, createSubscriptionPackage, updateSubscriptionPackage, deleteSubscriptionPackage, getPackageStatistics } from '../services/subscription-packages';

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    package_name: '',
    duration_type: 'daily',
    duration_value: 1,
    amount: '',
    description: '',
    features: {
      sms: 'unlimited',
      calls: 'unlimited',
      data_limit: 'unlimited',
      roaming: true,
      priority_support: true
    },
    is_active: true
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError('');
      const [packagesData, statsData] = await Promise.all([
        getSubscriptionPackages(),
        getPackageStatistics()
      ]);
      console.log('Packages API Response:', packagesData); // Debug log
      // Handle different response structures
      const packagesArray = packagesData?.data || packagesData || [];
      setPackages(Array.isArray(packagesArray) ? packagesArray : []);
      setStatistics(statsData);
    } catch (error) {
      setError('Failed to fetch packages');
      console.error('Failed to fetch packages:', error);
      setPackages([]); // Ensure packages is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFeatureChange = (feature, value) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPackage) {
        await updateSubscriptionPackage(editingPackage.id, formData);
        setSuccess('Package updated successfully');
      } else {
        await createSubscriptionPackage(formData);
        setSuccess('Package created successfully');
      }
      setIsModalOpen(false);
      resetForm();
      fetchPackages();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(`Failed to ${editingPackage ? 'update' : 'create'} package`);
      console.error('Failed to save package:', error);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      package_name: pkg.package_name,
      duration_type: pkg.duration_type,
      duration_value: pkg.duration_value,
      amount: pkg.amount,
      description: pkg.description,
      features: pkg.features,
      is_active: pkg.is_active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await deleteSubscriptionPackage(id);
        setSuccess('Package deleted successfully');
        fetchPackages();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Failed to delete package');
        console.error('Failed to delete package:', error);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      package_name: '',
      duration_type: 'daily',
      duration_value: 1,
      amount: '',
      description: '',
      features: {
        sms: 'unlimited',
        calls: 'unlimited',
        data_limit: 'unlimited',
        roaming: true,
        priority_support: true
      },
      is_active: true
    });
    setEditingPackage(null);
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const getDurationLabel = (type, value) => {
    const types = {
      daily: 'Day',
      weekly: 'Week',
      monthly: 'Month',
      yearly: 'Year'
    };
    return `${value} ${types[type]}${value > 1 ? 's' : ''}`;
  };

  const statsCards = statistics ? [
    { 
      label: 'Total Packages', 
      value: statistics.total_packages || '0', 
      icon: '📦', 
      color: 'bg-blue-500',
      subtext: `${statistics.active_packages || '0'} active`
    },
    { 
      label: 'Active Packages', 
      value: statistics.active_packages || '0', 
      icon: '✅', 
      color: 'bg-green-500',
      subtext: `${statistics.inactive_packages || '0'} inactive`
    },
    { 
      label: 'Average Price', 
      value: `KSH ${parseFloat(statistics.average_price || 0).toFixed(2)}`, 
      icon: '💰', 
      color: 'bg-purple-500',
      subtext: `Min: KSH ${statistics.min_price || '0'}`
    },
    { 
      label: 'Max Price', 
      value: `KSH ${parseFloat(statistics.max_price || 0).toFixed(2)}`, 
      icon: '💎', 
      color: 'bg-yellow-500',
      subtext: `${statistics.duration_types_count || '0'} duration types`
    },
  ] : [];

  return (
    <div>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Packages</h1>
          <p className="text-gray-600 mt-2">Manage your subscription packages and pricing</p>
        </div>
        <button
          onClick={openModal}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          + New Package
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  {stat.subtext && (
                    <p className="text-xs text-gray-500 mt-1">{stat.subtext}</p>
                  )}
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center text-2xl flex-shrink-0`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : packages && Array.isArray(packages) && packages.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Packages Yet</h3>
          <p className="text-gray-600 mb-6">Create your first subscription package to get started</p>
          <button
            onClick={openModal}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
          >
            Create Package
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages && Array.isArray(packages) && packages.map((pkg) => (
            <div key={pkg.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{pkg.package_name}</h3>
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded">
                      {getDurationLabel(pkg.duration_type, pkg.duration_value)}
                    </span>
                  </div>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{pkg.description}</p>

                <div className="mb-4">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    KSH {parseFloat(pkg.amount).toFixed(2)}
                  </div>
                </div>

                <div className="border-t pt-4 mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Features:</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-center">
                      <span className="mr-2">📱</span>
                      SMS: {pkg.features?.sms || 'N/A'}
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">📞</span>
                      Calls: {pkg.features?.calls || 'N/A'}
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">📊</span>
                      Data: {pkg.features?.data_limit || 'N/A'}
                    </li>
                    {pkg.features?.roaming && (
                      <li className="flex items-center">
                        <span className="mr-2">🌍</span>
                        Roaming enabled
                      </li>
                    )}
                    {pkg.features?.priority_support && (
                      <li className="flex items-center">
                        <span className="mr-2">⭐</span>
                        Priority support
                      </li>
                    )}
                  </ul>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-2 px-4 rounded transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(pkg.id)}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 font-medium py-2 px-4 rounded transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingPackage ? 'Edit Package' : 'Create New Package'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Name *
                  </label>
                  <input
                    type="text"
                    name="package_name"
                    value={formData.package_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration Type *
                    </label>
                    <select
                      name="duration_type"
                      value={formData.duration_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration Value *
                    </label>
                    <input
                      type="number"
                      name="duration_value"
                      value={formData.duration_value}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (KSH) *
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features
                  </label>
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">SMS</label>
                      <input
                        type="text"
                        value={formData.features.sms}
                        onChange={(e) => handleFeatureChange('sms', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., unlimited, 100, 500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Calls</label>
                      <input
                        type="text"
                        value={formData.features.calls}
                        onChange={(e) => handleFeatureChange('calls', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., unlimited, 100 minutes"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Data Limit</label>
                      <input
                        type="text"
                        value={formData.features.data_limit}
                        onChange={(e) => handleFeatureChange('data_limit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., unlimited, 10GB, 50GB"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="roaming"
                        checked={formData.features.roaming}
                        onChange={(e) => handleFeatureChange('roaming', e.target.checked)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="roaming" className="ml-2 text-sm text-gray-700">
                        Roaming enabled
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="priority_support"
                        checked={formData.features.priority_support}
                        onChange={(e) => handleFeatureChange('priority_support', e.target.checked)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="priority_support" className="ml-2 text-sm text-gray-700">
                        Priority support
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                    Package is active
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {editingPackage ? 'Update Package' : 'Create Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packages;
